
import {
  computed, defineComponent, onMounted, ref, watch, reactive, onBeforeUnmount,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import RefreshPull from 'pull-refresh-vue3';
import { descriptionWithLink } from '@/helpers/utils';
// eslint-disable-next-line import/no-cycle
import { useDriveStore } from '../store/driveStore';
// eslint-disable-next-line import/no-cycle
import { useMainStore } from '@/store/mainStore';
import {
  APPLICATION_SETTINGS_TYPE, CASE_VIEW_NAVIGATION, NAVIGATION_PAGES, PHOTO_VIEW_TYPE, SHARE_TYPES,
} from '@/types/enums';
import {
  IMedia, IPhoto, ICase, IFolder, IReactiveSharePopUp, ISharedLable,
} from '@/types/interfaces';
// import AppLazyImage from '@/components/UI/AppLazyImage.vue';
import DriveSharedBar from '@/modules/drive/components/DriveSharedBar.vue';
import DrivePhotoCarousel from '../components/DrivePhotoCarousel.vue';
import DriveOverview from '../components/ViewSubpages/DriveOverviewSubpage.vue';
import DriveFiles from '../components/ViewSubpages/DriveFilesSubpage.vue';
import { lowResUrlByViewTypeByUrl } from '@/helpers/lowResPhotoUrl';
// eslint-disable-next-line import/no-cycle
import MonitoringService from '@/services/MonitoringService';
import AppReorderPhoto from '@/components/AppReorderPhoto.vue';
import AppSkeletonCaseView from '@/components/skeletons/AppSkeletonCaseView.vue';
import UsePullToRefresh from '@/composables/UsePullToRefresh';
import MoveToPopUp from '@/components/BottomPopUp/PopUpTypes/MoveTo/MoveToPopUp.vue';
import DriveCasePhotos from '@/modules/drive/components/desktop/cases/DriveCasePhotos.vue';
import SharePopUp from '@/components/BottomPopUp/PopUpTypes/Share/SharePopUp.vue';
import DriveTooltip from '../components/UI/DriveTooltip.vue';
import AppHeaderUploadProgress from '@/components/AppHeaderUploadProgress.vue';

export default defineComponent({
  components: {
    DriveSharedBar,
    DrivePhotoCarousel,
    DriveOverview,
    DriveFiles,
    AppSkeletonCaseView,
    RefreshPull,
    MoveToPopUp,
    DriveCasePhotos,
    SharePopUp,
    DriveTooltip,
    AppHeaderUploadProgress,
    AppReorderPhoto,
  },
  emits: ['open-share', 'saveCaseAsCopy', 'deleteCase'],
  props: {
    showSkeleton: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    const driveStore = useDriveStore();
    const mainStore = useMainStore();
    const route = useRoute();
    const router = useRouter();
    const positionData = ref({});
    const reorderPhotoView = ref(false);
    const photoUploading = computed(() => mainStore.mediaUploading);

    const photoUrl = computed(() => {
      if (driveStore.currentCase.owner.photo?.url) {
        return lowResUrlByViewTypeByUrl(driveStore.currentCase.owner.photo.url, PHOTO_VIEW_TYPE.WHSML);
      }
      // eslint-disable-next-line global-require
      return require('@/assets/img/mockup/profile.svg');
    });

    const isNetwork = computed(() => route.meta.isNetwork);
    const isCaseSettingsPopUp = ref(false);
    const isMoveToCasePopUp = ref(false);

    const currentFolderText = computed(() => {
      if (!driveStore.currentCase?.path?.length) {
        return '';
      }
      return driveStore.currentCase.path[driveStore.currentCase.path.length - 1].name;
      // if (root.value || !currentFolder.value) {
      //   return '';
      // }
      // return currentFolder.value.name;
    });
    const casePath = computed(() => driveStore.currentCase.path);
    const myCase = computed(() => driveStore.currentCase.owner.accountId === mainStore.user.accountId
      || driveStore.currentCase.owner.accountId === mainStore.boundCorporate.internalId);

    const createdDate = computed(() => new Date(driveStore.currentCase.created as any).toLocaleDateString('en-US', {
      month: 'long', day: '2-digit', year: 'numeric',
    }));

    const updatedDate = computed(() => new Date(driveStore.currentCase.updated as any).toLocaleDateString('en-US', {
      month: 'long', day: '2-digit', year: 'numeric',
    }));

    const fullName = computed(() => `${driveStore.currentCase.owner.firstName} ${driveStore.currentCase.owner.lastName}`);
    const showDots = computed(() => driveStore.currentCase?.path && driveStore.currentCase.path.length > 1);

    const dataLoading = ref<boolean>(true);
    const currentSubpage = ref<CASE_VIEW_NAVIGATION>(CASE_VIEW_NAVIGATION.OVERVIEW);

    const isPublicCases = computed(
      () => mainStore.applicationSettings.find((feature) => feature.name === APPLICATION_SETTINGS_TYPE.SHOW_PUBLIC_CASES_FEED)?.enabled,
    );

    function selectPhoto(photo: IMedia): void {
      driveStore.appPhotoViewMedia = photo;
    }

    function selectVideo(video: IMedia): void {
      driveStore.appPhotoViewMedia = video;
    }

    function saveCaseAsCopy() {
      emit('saveCaseAsCopy');
    }

    function editCase() {
      driveStore.currentCase.filesAmount = driveStore.currentCase.files.length;
      driveStore.currentCase.photosAmount = driveStore.currentCase.photos.length;
      // hidePopUp();
      router.push(`/drive/case/${driveStore.currentCase.id}/edit`);
    }

    function deleteCase() {
      emit('deleteCase');
    }

    function reorderPhoto() {
      reorderPhotoView.value = true;
    }

    function resolvePhoto(item: { name: string, photo: IPhoto, isGroup: boolean }): string {
      if (item.photo?.url) {
        return lowResUrlByViewTypeByUrl(item.photo.url, PHOTO_VIEW_TYPE.WHSML);
      }
      if (item.isGroup) {
        // eslint-disable-next-line global-require
        return require('@/assets/img/mockup/Group.svg');
      }
      // eslint-disable-next-line global-require
      return require('@/assets/img/mockup/profile.svg');
    }

    const sharedPopUp: IReactiveSharePopUp = reactive({
      item: null,
      type: null,
    });

    function openSharePopUp(item: IMedia | IFolder | ICase, type: SHARE_TYPES) {
      sharedPopUp.item = item;
      sharedPopUp.type = type;
    }

    function closeSharePopUp() {
      sharedPopUp.item = null;
      sharedPopUp.type = null;
    }

    function setItemShared() {
      // is declared but it is not using now. Current code will not work correctly with new app logic
      // sharedPopUp.item!.shared = true
    }

    function setSharedLabels(labels: ISharedLable[]) {
      // is declared but it is not using now. Current code will not work correctly with new app logic
      // sharedPopUp.item!.sharedLabels = labels;
    }

    function removeSharedItemFromStore() {
      closeSharePopUp();
      driveStore.fullscreenFile = undefined;
      driveStore.fullscreenPhoto = undefined;
      driveStore.appPhotoViewMedia = undefined;
    }

    function fetchCase(isAfterCreate = false) {
      dataLoading.value = true;

      driveStore.getCaseById(route.params.id as string, isNetwork.value, isAfterCreate)
        .then((res) => {
          console.log('res: ', res);
          /**
           * Find index of case in driveStore.cases.data and amend it with fresh case data from response
           * specially photo array that now contain correct photo ids
           * after all remove query param ?create=1 from url
           * */
          const idx = driveStore.cases.data.findIndex((el) => el.id === res.id);
          driveStore.cases.data[idx] = { ...res };
          router.replace({ ...router.currentRoute, query: {} });

          const sharedCaseInList = driveStore.networkCases.data.filter((el) => el.id === res.id)[0];
          driveStore.currentCase = {
            ...res,
            root: sharedCaseInList?.root || false,
            shareSettings: sharedCaseInList?.shareSettings || [],
          };
          driveStore.photoList = driveStore.currentCase.photos;
          if (!myCase.value) {
            router.push(`/drive/case/network/${driveStore.currentCase.id}/view/`);
          }
        })
        .finally(() => {
          dataLoading.value = false;
        });
    }

    function viewAllCases() {
      if (isNetwork.value) {
        router.push('/drive/network/cases');
      } else {
        router.push('/drive/my/cases');
      }
    }
    watch(() => route.params.id, () => {
      if (route.params.id && route.meta.navigationPage === NAVIGATION_PAGES.CASE_VIEW) {
        fetchCase();
      }
    });

    function openMovePopUp() {
      isCaseSettingsPopUp.value = false;
      isMoveToCasePopUp.value = true;
    }

    function viewProfile(): void {
      if (mainStore.user.accountId === driveStore.currentCase.owner.accountId) {
        return;
      }
      router.push(`/profile/${driveStore.currentCase.owner.accountId}`);
    }
    async function fetchCacheData() {
      if (isNetwork.value) {
        return driveStore.fetchNetworkCases();
      }
      return driveStore.fetchCases();
    }

    function refreshCases() {
      if (isNetwork.value) {
        driveStore.networkCases.page = 0;
        driveStore.networkCases.lastPage = -1;
      } else {
        driveStore.cases.page = 0;
        driveStore.cases.lastPage = -1;
      }

      return fetchCacheData().then(() => fetchCase());
    }

    function getPosition(event) {
      const x = event.offsetX;
      const y = event.clientY;
      positionData.value = { x, y };
    }

    const {
      loadingPull, loosingText, successText, pullingText, loadingText, onRefresh,
    } = UsePullToRefresh(refreshCases);

    watch(() => route.query.create, (newValue) => {
      if (newValue === '1') {
        fetchCase(true);
      }
    });

    onMounted(() => {
      MonitoringService.sendPageMountedSpeed();
      /**
       * isAfterCreate - boolean to check if we come to view mode just after new case creation
       * then pass this value to @function fetchCase
       * */
      const isAfterCreate = route.query?.create === '1';
      fetchCase(isAfterCreate);
    });

    onBeforeUnmount(() => {
      driveStore.resetCurrentCase();
    });
    return {
      dataLoading,
      isNetwork,
      currentSubpage,
      driveStore,
      selectPhoto,
      resolvePhoto,
      selectVideo,
      CASE_VIEW_NAVIGATION,
      SHARE_TYPES,

      loadingPull,
      loosingText,
      successText,
      pullingText,
      loadingText,
      onRefresh,
      isPublicCases,
      mainStore,
      viewAllCases,
      viewProfile,
      photoUrl,
      fullName,
      createdDate,
      updatedDate,
      openSharePopUp,
      sharedPopUp,
      closeSharePopUp,
      setItemShared,
      setSharedLabels,
      removeSharedItemFromStore,
      saveCaseAsCopy,
      editCase,
      deleteCase,
      getPosition,
      positionData,
      isCaseSettingsPopUp,
      isMoveToCasePopUp,
      openMovePopUp,
      currentFolderText,
      showDots,
      casePath,
      photoUploading,
      reorderPhotoView,
      reorderPhoto,
      myCase,
      descriptionWithLink,
    };
  },
});
